import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { racesAPI, picksAPI } from '../services/api';
import PickBreakdown from '../components/PickBreakdown';

function Results() {
  const navigate = useNavigate();
  const [races, setRaces] = useState([]);
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' or 'winners'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load completed races
      const racesResponse = await racesAPI.getRaces({ status: 'completed' });
      const completedRaces = racesResponse.success ? racesResponse.data.races : [];
      
      // Load user's completed picks
      const picksResponse = await picksAPI.getMyPicks({ status: 'completed' });
      const completedPicks = picksResponse.success ? picksResponse.data.picks : [];
      
      setRaces(completedRaces);
      setPicks(completedPicks);
    } catch (error) {
      console.error('Failed to load results:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPicksForRace = (raceId) => {
    return picks.filter(pick => pick.raceId === raceId);
  };

  const getFilteredPicks = (racePicks) => {
    if (filter === 'winners') {
      return racePicks.filter(pick => pick.actualPayout > 0);
    }
    return racePicks;
  };

  const getRaceResultsSummary = (race) => {
    if (!race.results) return '';
    
    // Check if new format (standings) or legacy format (winner/place/show)
    if (race.results.standings && race.results.standings.length > 0) {
      const top3 = race.results.standings.slice(0, 3);
      const parts = [];
      if (top3[0]) parts.push(`🥇 ${top3[0].horseName}`);
      if (top3[1]) parts.push(`🥈 ${top3[1].horseName}`);
      if (top3[2]) parts.push(`🥉 ${top3[2].horseName}`);
      return parts.join(' • ');
    } else {
      // Legacy format
      const { winner, place, show } = race.results;
      const parts = [];
      if (winner) parts.push(`🥇 ${winner.horseName}`);
      if (place) parts.push(`🥈 ${place.horseName}`);
      if (show) parts.push(`🥉 ${show.horseName}`);
      return parts.join(' • ');
    }
  };

  const renderFullStandings = (race) => {
    if (!race.results) return null;
    
    // Check if we have the new standings format
    if (race.results.standings && race.results.standings.length > 0) {
      return (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '8px'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 'bold' }}>
            Complete Standings
          </h4>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {race.results.standings.map((standing, idx) => (
              <div
                key={standing.horseId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: idx < 3 ? '#f5f5f5' : 'transparent',
                  borderRadius: '4px',
                  fontWeight: idx < 3 ? 'bold' : 'normal'
                }}
              >
                <div style={{ width: '60px', fontSize: '1rem' }}>
                  {idx === 0 && '🥇'}
                  {idx === 1 && '🥈'}
                  {idx === 2 && '🥉'}
                  {idx > 2 && `${idx + 1}.`}
                </div>
                <div style={{ flex: 1 }}>
                  {standing.horseName}
                </div>
              </div>
            ))}
          </div>
          
          {/* Display payout information if available */}
          {race.results.payouts && (
            <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e0e0e0' }}>
              <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', fontWeight: 'bold' }}>
                Payout Information
              </h5>
              <div style={{ fontSize: '0.9rem', display: 'grid', gap: '0.5rem' }}>
                {race.results.payouts.win && race.results.payouts.win.payoutAmount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                    <span>Win ({race.results.payouts.win.horseName}):</span>
                    <strong>${race.results.payouts.win.payoutAmount.toFixed(2)}</strong>
                  </div>
                )}
                {race.results.payouts.place && race.results.payouts.place.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                    <span>Place:</span>
                    <div style={{ textAlign: 'right' }}>
                      {race.results.payouts.place.map((p, idx) => (
                        <div key={idx}>
                          ${p.payoutAmount.toFixed(2)} ({p.horseName})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {race.results.payouts.show && race.results.payouts.show.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                    <span>Show:</span>
                    <div style={{ textAlign: 'right' }}>
                      {race.results.payouts.show.map((s, idx) => (
                        <div key={idx}>
                          ${s.payoutAmount.toFixed(2)} ({s.horseName})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {race.results.payouts.exacta && race.results.payouts.exacta.payoutAmount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                    <span>Exacta:</span>
                    <strong>${race.results.payouts.exacta.payoutAmount.toFixed(2)}</strong>
                  </div>
                )}
                {race.results.payouts.trifecta && race.results.payouts.trifecta.payoutAmount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                    <span>Trifecta:</span>
                    <strong>${race.results.payouts.trifecta.payoutAmount.toFixed(2)}</strong>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }
    
    return null;
  };

  if (loading) {
    return <div className="loading">Loading results...</div>;
  }

  return (
    <div className="results-page">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem' 
      }}>
        <h1 style={{ margin: 0 }}>🏁 Race Results</h1>
        <div>
          <label style={{ marginRight: '0.5rem', fontWeight: 'bold' }}>Show:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
              backgroundColor: 'white',
              cursor: 'pointer',
            }}
          >
            <option value="all">All My Picks</option>
            <option value="winners">Winning Picks Only</option>
          </select>
        </div>
      </div>

      {races.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#666' }}>
            No completed races yet. Check back after races finish!
          </p>
        </div>
      ) : (
        <div>
          {races.map((race) => {
            const racePicks = getPicksForRace(race.raceId);
            const filteredPicks = getFilteredPicks(racePicks);
            
            if (filter === 'winners' && filteredPicks.length === 0) {
              return null; // Skip races with no winning picks when filtering
            }

            return (
              <div key={race.raceId} className="card" style={{ marginBottom: '2rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'start',
                  marginBottom: '1rem',
                  paddingBottom: '1rem',
                  borderBottom: '2px solid #f0f0f0',
                }}>
                  <div style={{ flex: 1 }}>
                    <h2 
                      style={{ 
                        margin: '0 0 0.5rem 0',
                        cursor: 'pointer',
                        color: '#2196F3',
                        display: 'inline-block',
                      }}
                      onClick={() => navigate(`/races/${race.raceId}`)}
                    >
                      {race.raceName}
                    </h2>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                      📍 {race.venue} {race.location && `• ${race.location}`} • {new Date(race.raceDate).toLocaleString()}
                    </div>
                    {race.results && (
                      <>
                        <div style={{ 
                          fontSize: '0.95rem',
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px',
                          display: 'inline-block',
                        }}>
                          {getRaceResultsSummary(race)}
                        </div>
                        {renderFullStandings(race)}
                      </>
                    )}
                  </div>
                  
                  {racePicks.length > 0 && (
                    <div style={{
                      textAlign: 'right',
                      padding: '0.5rem 1rem',
                      backgroundColor: filteredPicks.some(p => p.actualPayout > 0) ? '#e8f5e9' : '#ffebee',
                      borderRadius: '8px',
                    }}>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                        My Result
                      </div>
                      <div style={{
                        fontSize: '1.3rem',
                        fontWeight: 'bold',
                        color: filteredPicks.some(p => p.actualPayout > 0) ? '#2e7d32' : '#c62828',
                      }}>
                        {filteredPicks.reduce((sum, p) => sum + (p.actualPayout || 0), 0) > 0 ? '🏆 ' : ''}
                        $
                        {filteredPicks.reduce((sum, p) => sum + (p.actualPayout || 0), 0).toFixed(2)}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {filteredPicks.length} {filteredPicks.length === 1 ? 'pick' : 'picks'}
                      </div>
                    </div>
                  )}
                </div>

                {racePicks.length === 0 ? (
                  <div style={{ 
                    padding: '1rem',
                    textAlign: 'center',
                    color: '#999',
                    fontStyle: 'italic',
                    backgroundColor: '#fafafa',
                    borderRadius: '4px',
                  }}>
                    You didn't place any picks for this race
                  </div>
                ) : filteredPicks.length === 0 ? (
                  <div style={{ 
                    padding: '1rem',
                    textAlign: 'center',
                    color: '#999',
                    fontStyle: 'italic',
                    backgroundColor: '#fafafa',
                    borderRadius: '4px',
                  }}>
                    No winning picks for this race
                  </div>
                ) : (
                  <div>
                    <h3 style={{ 
                      fontSize: '1rem',
                      marginBottom: '1rem',
                      color: '#666',
                    }}>
                      My Picks:
                    </h3>
                    {filteredPicks.map((pick) => (
                      <div
                        key={pick.pickId}
                        style={{
                          marginBottom: '1rem',
                          padding: '1rem',
                          backgroundColor: pick.actualPayout > 0 ? '#f9fff9' : '#fff9f9',
                          border: `1px solid ${pick.actualPayout > 0 ? '#c8e6c9' : '#ffcdd2'}`,
                          borderRadius: '8px',
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.5rem',
                        }}>
                          <div>
                            <strong>Horses:</strong>{' '}
                            {pick.horses?.map(h => h.horseName).join(', ') || pick.horseName || 'N/A'}
                          </div>
                          <div>
                            <span style={{ 
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              backgroundColor: pick.actualPayout > 0 ? '#4CAF50' : '#f44336',
                              color: 'white',
                              fontSize: '0.9rem',
                              fontWeight: 'bold',
                            }}>
                              {pick.actualPayout > 0 ? '✓ WIN' : '✗ LOSS'}
                            </span>
                          </div>
                        </div>
                        
                        <div style={{ 
                          fontSize: '0.9rem', 
                          color: '#666',
                          marginBottom: '0.5rem',
                        }}>
                          Payout: <strong style={{
                            color: pick.actualPayout > 0 ? '#4CAF50' : '#f44336',
                          }}>
                            ${(pick.actualPayout || 0).toFixed(2)}
                          </strong>
                        </div>

                        {pick.actualPayout > 0 && pick.payoutBreakdown && (
                          <PickBreakdown pick={pick} expanded={true} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="card" style={{ marginTop: '2rem', backgroundColor: '#f5f5f5' }}>
        <h3 style={{ marginTop: 0 }}>📊 My Overall Stats</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
        }}>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Total Picks</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{picks.length}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Winning Picks</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4CAF50' }}>
              {picks.filter(p => p.actualPayout > 0).length}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Total Winnings</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4CAF50' }}>
              ${picks.reduce((sum, p) => sum + (p.actualPayout || 0), 0).toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Win Rate</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              {picks.length > 0
                ? ((picks.filter(p => p.actualPayout > 0).length / picks.length) * 100).toFixed(1)
                : 0}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Results;
